import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ChatParticipant, ChatSession, Message, ParticipantTranscript, QueuedMessage, TranscriptGap } from '@localagent/sdk';

// (sdk ADR-377c6)
export function useChatParticipants(chat: ChatSession | null): ChatParticipant[] {
  const [participants, setParticipants] = useState<ChatParticipant[]>(() => chat?.participants ?? []);
  useEffect(() => {
    if (!chat) {
      setParticipants([]);
      return;
    }
    setParticipants(chat.participants);
    return chat.onParticipants(setParticipants);
  }, [chat]);
  return participants;
}

const EMPTY_MESSAGES: readonly Message[] = [];

export interface ParticipantTranscriptView {
  messages: readonly Message[];
  loaded: boolean;
  hasOlder: boolean;
  loadOlder: () => Promise<void>;
  gap: TranscriptGap | null;
  loadNewer: () => Promise<void>;
  reportVisibleMessages: (visibleMessageIds: readonly string[] | null) => void;
  error: Error | null;
}

// (sdk ADR-377c6, sdk ADR-f43c7)
export function useParticipantTranscript(chat: ChatSession | null, participantId: string): ParticipantTranscriptView {
  const [transcript, setTranscript] = useState<ParticipantTranscript | null>(null);
  const [, setVersion] = useState(0);
  const [error, setError] = useState<Error | null>(null);
  useEffect(() => {
    if (!chat || chat.isDraft || chat.isDisconnected) {
      setTranscript(null);
      return;
    }
    let held: ParticipantTranscript;
    try {
      held = chat.participantTranscript(participantId);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      return;
    }
    setTranscript(held);
    setError(null);
    const unsubscribe = held.subscribe(() => setVersion((v) => v + 1));
    held.loadTail().catch((err: unknown) => setError(err instanceof Error ? err : new Error(String(err))));
    return () => {
      unsubscribe();
      if (!chat.isDisconnected) {
        held.setViewportDemand(null);
        chat.releaseParticipantTranscript(participantId);
      }
      setTranscript(null);
    };
  }, [chat, participantId]);

  const reportVisibleMessages = useCallback(
    (visibleMessageIds: readonly string[] | null) => {
      if (!transcript) return;
      const shown = new Set(visibleMessageIds);
      const indices = transcript.messages.filter((m) => shown.has(m.id)).map((m) => m.message_index);
      transcript.setViewportDemand(
        indices.length > 0
          ? { oldestMessageIndex: Math.min(...indices), newestMessageIndex: Math.max(...indices) }
          : null,
      );
    },
    [transcript],
  );

  const loadOlder = useCallback(async () => {
    if (!transcript) return;
    await transcript.loadOlder();
  }, [transcript]);
  const loadNewer = useCallback(async () => {
    if (!transcript) return;
    await transcript.loadNewer();
  }, [transcript]);
  const messages = transcript?.messages ?? EMPTY_MESSAGES;
  const loaded = transcript?.loaded ?? false;
  const hasOlder = transcript?.hasOlder ?? false;
  const currentGap = transcript?.gap ?? null;
  const gapAfter = currentGap?.afterMessageIndex;
  const gapBefore = currentGap?.beforeMessageIndex;
  const gap = useMemo(
    () => (gapAfter !== undefined && gapBefore !== undefined ? { afterMessageIndex: gapAfter, beforeMessageIndex: gapBefore } : null),
    [gapAfter, gapBefore],
  );

  return useMemo(
    () => ({ messages, loaded, hasOlder, loadOlder, gap, loadNewer, reportVisibleMessages, error }),
    [messages, loaded, hasOlder, loadOlder, gap, loadNewer, reportVisibleMessages, error],
  );
}

// (sessions ADR-d28b1, sdk ADR-377c6)
export function useParticipantQueue(chat: ChatSession | null, participantId: string): QueuedMessage[] {
  const [queue, setQueue] = useState<QueuedMessage[]>([]);
  useEffect(() => {
    if (!chat || chat.isDraft || chat.isDisconnected) {
      setQueue([]);
      return;
    }
    setQueue(chat.participantQueueOf(participantId));
    return chat.onParticipantQueue(participantId, setQueue);
  }, [chat, participantId]);
  return queue;
}
